import { Box, Drawer, Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useReservationInfo } from '../../../ReservationInfoContext';
import { useAuthContext } from '../../../auth/useAuthContext';
import Logo from '../../../components/logo';
import { NavSectionVertical } from '../../../components/nav-section';
import Scrollbar from '../../../components/scrollbar';
import { NAV } from '../../../config-global';
import useResponsive from '../../../hooks/useResponsive';
import { routeRoleAsAdmin } from '../../../utils/mrr/userConstants';
import { navConfigAdmin } from '../../main/nav/config-navigation';
import NavAccount from './NavAccount';
import NavToggleButton from './NavToggleButton';
import createNavConfig from './config-navigation';
import { selectReservationMenuLabelToRemove, filterNavItems, selectListingMenuLabelToRemove } from './navHelpers';
import { useListingInfo } from '../../../ListingInfoContext';

type Props = {
  openNav: boolean;
  onCloseNav: VoidFunction;
};

export default function NavVertical({ openNav, onCloseNav }: Props) {
  const {
    totalReservations,
    isLoading: isReservationInfoLoading,
    primaryReservation } = useReservationInfo()

  const {
    isLoading: isListingInfoLoading,
    singleListingId,
    totalListings
  } = useListingInfo()

  const navListKeysToRemove = useMemo(() => {
    let itemsToRemove: string[] = [];
    itemsToRemove = itemsToRemove.concat(selectListingMenuLabelToRemove(isListingInfoLoading, totalListings));
    itemsToRemove = itemsToRemove.concat(selectReservationMenuLabelToRemove(isReservationInfoLoading, totalReservations));
    return itemsToRemove;
  }, [isListingInfoLoading, isReservationInfoLoading, totalListings, totalReservations])

  const filteredNavConfig = useMemo(() => {
    return filterNavItems(  navListKeysToRemove,
                            createNavConfig(primaryReservation ? primaryReservation.name : '', singleListingId !== null ? singleListingId  : '' ))
  }, [navListKeysToRemove, primaryReservation, singleListingId])

  const { user } = useAuthContext()
  const { pathname } = useLocation();

  //const originRole = getSessionRole() // FOR THE EMULATOR
  const sessionCanSeeAdminPages = user ? routeRoleAsAdmin(user.role) : false;
  // const sessionCanSeeDevTab = allowRoleToSeeDevTab(originRole)

  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const navData = sessionCanSeeAdminPages ? navConfigAdmin : filteredNavConfig

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        spacing={1}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
        }}
      >
        <Logo full />

        <NavAccount />
      </Stack>

      <NavSectionVertical
        isLoading={isReservationInfoLoading || isListingInfoLoading}
        data={navData}
      />

      <Box sx={{ flexGrow: 1 }} />

    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_DASHBOARD },
      }}
    >
      <NavToggleButton />

      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              zIndex: 0,
              width: NAV.W_DASHBOARD,
              bgcolor: 'transparent',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV.W_DASHBOARD,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
